import { useTranslation } from 'react-i18next';
import { Typography, Card, CardContent, Stack, Box } from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { useParams, useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { BoxSeriesType, BoxType, QualityType } from '@/api/enum/common.enum';
import {
	BlindBoxDetailDTO,
	BlindBoxGameDTO,
	BoxFilter,
} from '@/api/DTO/exchange/box.interface';
import { useGetBoxTreasureListQuery } from '@/api/exchange/boxApiSlice';
import AddTreasureDialog from './components/AddTreasureDialog';
import SetSingleOrderDialog from './components/SetSingleOrderDialog';
import { showLabel } from '@/utils/helper';
import PublishedChip from '@/common/components/PublishedChip';
import { PUBLISHED_LISTB } from '@/common/constants/common';

export interface DataGrid {
	updatedAt?: string;
	createdAt?: string;
	publishedStartAt?: string;
	publishedEndAt?: string;
	id?: string;
	published?: number | string | boolean;
}

interface Props {
	details?: BlindBoxDetailDTO;
}

export default function TreasureListSummaryPage({ details }: Props) {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const { boxId } = useParams();

	const qualityList = [
		{ id: QualityType.Normal, name: t('chartletList.normal') },
		{ id: QualityType.Rare, name: t('chartletList.rare') },
		{ id: QualityType.Precious, name: t('chartletList.precious') },
		{ id: QualityType.SuperPrecious, name: t('chartletList.superPrecious') },
	];

	const defaultPropertyFilter: BoxFilter = {
		pagination: initialPagination,
		sortings: [],
		selectedList: [1],
		id: boxId || '',
	};

	const defaultValues: DefaultValues<BoxFilter> = defaultPropertyFilter;
	const methods = useForm<BoxFilter>({
		defaultValues,
	});

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as BoxFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const transformedSearchParams: BoxFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
		]),
	};

	const {
		data: propertySummary,
		isSuccess: isGetPropertySummarySuccess,
		refetch,
	} = useGetBoxTreasureListQuery(transformedSearchParams, {
		refetchOnMountOrArgChange: true,
	});

	useEffect(() => {
		refetch();
	}, [details?.boxType]);

	const transformResponse = useCallback((response?: BlindBoxGameDTO[]) => {
		if (response == null) return;
		const newResponse: DataGrid[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				// createdAt: !item.createdAt
				// 	? '-'
				// 	: dayjs(item.createdAt)
				// 			.locale('en')
				// 			.format(DISPLAY_DATETIME_12H_FORMAT),
				// updatedAt: !item.updatedAt
				// 	? '-'
				// 	: dayjs(item.updatedAt)
				// 			.locale('en')
				// 			.format(DISPLAY_DATETIME_12H_FORMAT),
			});
		});
		return newResponse;
	}, []); // watch fields change

	const handleLinkTo = (row: any) => {
		if (row.gameType === BoxSeriesType.Gallery) {
			window.open(
				`/treasury-manage/gallery-list/info/${row.gameSeriesId}/detail/${row.gameTreasureId}`,
				'_blank'
			);
		} else if (row.gameType === BoxSeriesType.Puzzle) {
			window.open(
				`/treasury-manage/puzzle-list/info/${row.gameSeriesId}/detail/${row.gameTreasureId}`,
				'_blank'
			);
		} else if (row.gameType === BoxSeriesType.Email) {
			window.open(
				`/treasury-manage/stamp-list/info/${row.gameSeriesId}/detail/${row.gameTreasureId}`,
				'_blank'
			);
		} else if (row.gameType === BoxSeriesType.Card) {
			window.open(
				`/treasury-manage/wish-list/info/${row.gameSeriesId}/detail/${row.gameTreasureId}`,
				'_blank'
			);
		} else if (row.gameType === BoxSeriesType.Chartlet) {
			window.open(
				`/treasury-manage/chartlet-list/info/${row.gameSeriesId}/detail/${row.gameTreasureId}`,
				'_blank'
			);
		}
	};

	const columns: GridColDef[] = [
		{
			field: 'gameTypeName',
			headerName: t('box.typeName'),
			flex: 10,
		},
		{
			field: 'gameSeriesName',
			headerName: t('box.seriesName'),
			flex: 10,
		},
		{
			field: 'published',
			headerName: t('box.seriesStatus'),
			flex: 10,
			renderCell: (params: any) => (
				<PublishedChip
					published={params.row.published}
					publishList={PUBLISHED_LISTB}
				/>
			),
		},
		{
			field: 'gameTreasureName',
			headerName: t('box.treasureName'),
			flex: 10,
			renderCell: (params: any) => (
				<span
					style={{ color: 'blue', cursor: 'pointer' }}
					onClick={() => handleLinkTo(params.row)}
				>
					{params.row.gameTreasureName}
				</span>
			),
		},
		{
			field: 'quality',
			headerName: t('chartletList.quality'),
			flex: 10,
			renderCell: (params: any) => showLabel(params.row.quality, qualityList),
		},
		{
			field: 'probabilityPercent',
			headerName: t('box.probability'),
			flex: 10,
		},
	];

	return (
		<Card sx={{ marginTop: 3 }}>
			<CardContent>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h6'>{t('chartletList.treasureList')}</Typography>
					{details?.boxType == BoxType.MixtureBox && (
						<Stack direction={'row'} alignItems={'center'} spacing={3}>
							<SetSingleOrderDialog />
							<AddTreasureDialog />
						</Stack>
					)}
				</Box>
				<FormProvider {...methods}>
					<Box marginTop={2}>
						<AppDataGrid
							loading={!isGetPropertySummarySuccess}
							rows={transformResponse(propertySummary?.data?.items) ?? []}
							rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
							columns={
								details?.boxType == BoxType.SeriesBox
									? [...columns.slice(1)]
									: columns
							}
							getRowId={(row) => row.gameTreasureId}
						/>
					</Box>
				</FormProvider>
			</CardContent>
		</Card>
	);
}
